import { AudioDevice } from '../engine/AudioDevice';
import { AudioResourcePool } from '../engine/AudioResourcePool';
import { BitmapFontResource } from '../engine/BitmapFontResource';
import { BitmapFontResourcePool } from '../engine/BitmapFontResourcePool';
import { BlendResource } from '../engine/BlendResource';
import { BlendResourcePool } from '../engine/BlendResourcePool';
import { EventStream } from '../engine/EventStreamSubscription';
import { IConcreteResourceCache } from '../engine/IResourceCache';
import { JSONResource } from '../engine/JSONResource';
import { JSONResourcePool } from '../engine/JSONResourcePool';
import { Platform } from '../engine/Platform';
import { RenderDevice } from '../engine/RenderDevice';
import { SceneResource } from '../engine/SceneResource';
import { SceneResourcePool } from '../engine/SceneResourcePool';
import { ShaderResource } from '../engine/ShaderResource';
import { ShaderResourcePool } from '../engine/ShaderResourcePool';
import { ShaderSourceResource } from '../engine/ShaderSourceResource';
import { ShaderSourceResourcePool } from '../engine/ShaderSourceResourcePool';
import { SpriteResource } from '../engine/SpriteResource';
import { SpriteResourcePool } from '../engine/SpriteResourcePool';
import { TextureResource } from '../engine/TextureResource';
import { TextureResourcePool } from '../engine/TextureResourcePool';
import { TtfFontResource } from '../engine/TtfFontResource';
import { TtfFontResourcePool } from '../engine/TtfFontResourcePool';
import { VideoResource } from '../engine/VideoResource';
import { VideoResourcePoolCanvas } from '../engine/VideoResourcePoolCanvas';
import { VideoResourcePoolWebGL } from '../engine/VideoResourcePoolWebGL';
import { IAssetsUrlFormatter } from '../helpers/assets-url.formatter';

export class ApplicationResourceCache {
  private assetsUrlFormatter?: IAssetsUrlFormatter;

  constructor(
    private applicationResourceCache: IConcreteResourceCache,
    private getAssetsUrlFormatter?: () => IAssetsUrlFormatter | null
  ) {}

  public async registerPools(platform: Platform): Promise<void> {
    const renderDevice = platform.videoSystem.renderDevice;
    const audioDevice = platform.audioSystem.audioDevice;

    this.assetsUrlFormatter = this.getAssetsUrlFormatter?.() || undefined;

    this.registerVideoPools(platform);
    this.registerTexturePool(renderDevice);
    this.registerBlendPool(renderDevice);
    this.registerSpritePool();
    this.registerScenePool();
    this.registerFontPools(renderDevice);
    this.registerAudioPools(audioDevice);
    this.registerJSONPool();
  }

  public get resourceCache(): IConcreteResourceCache {
    return this.applicationResourceCache;
  }

  public get beforeLoad(): EventStream<string> {
    return this.applicationResourceCache.beforeLoad;
  }

  private registerVideoPools(platform: Platform): void {
    const renderDevice = platform.videoSystem.renderDevice;

    if (platform.videoSystem.isWebGL) {
      this.applicationResourceCache.registerPool(
        ShaderSourceResource.TypeId,
        new ShaderSourceResourcePool()
      );

      this.applicationResourceCache.registerPool(
        ShaderResource.TypeId,
        new ShaderResourcePool(this.applicationResourceCache, renderDevice)
      );

      this.applicationResourceCache.registerPool(
        VideoResource.TypeId,
        new VideoResourcePoolWebGL(platform, renderDevice, this.assetsUrlFormatter)
      );
      return;
    }

    this.applicationResourceCache.registerPool(
      VideoResource.TypeId,
      new VideoResourcePoolCanvas(platform, this.assetsUrlFormatter)
    );
  }

  private registerTexturePool(renderDevice: RenderDevice): void {
    this.applicationResourceCache.registerPool(
      TextureResource.TypeId,
      new TextureResourcePool(renderDevice, this.assetsUrlFormatter)
    );
  }

  private registerBlendPool(renderDevice: RenderDevice): void {
    this.applicationResourceCache.registerPool(
      BlendResource.TypeId,
      new BlendResourcePool(renderDevice)
    );
  }

  private registerSpritePool(): void {
    this.applicationResourceCache.registerPool(
      SpriteResource.TypeId,
      new SpriteResourcePool(this.applicationResourceCache)
    );
  }

  private registerScenePool(): void {
    this.applicationResourceCache.registerPool(SceneResource.TypeId, new SceneResourcePool());
  }

  private registerFontPools(renderDevice: RenderDevice): void {
    this.applicationResourceCache.registerPool(
      TtfFontResource.TypeId,
      new TtfFontResourcePool(renderDevice, this.assetsUrlFormatter)
    );

    this.applicationResourceCache.registerPool(
      BitmapFontResource.TypeId,
      new BitmapFontResourcePool(this.applicationResourceCache)
    );
  }

  private registerAudioPools(audioDevice: AudioDevice): void {
    const audioResourcePool = new AudioResourcePool(audioDevice, this.assetsUrlFormatter);

    this.applicationResourceCache.registerPool(AudioResourcePool.Buffer, audioResourcePool);
    this.applicationResourceCache.registerPool(AudioResourcePool.Stream, audioResourcePool);
  }

  private registerJSONPool(): void {
    this.applicationResourceCache.registerPool(JSONResource.TypeId, new JSONResourcePool());
  }
}
